/**
 * 知识图谱状态管理
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { create } from 'zustand'
import { memoryAdminApi } from '../services/memory-admin-api'
import type { 
  Entity, 
  Relationship, 
  GraphNode as ApiGraphNode
} from '../types/memory'



// ==================== 类型定义 ====================

/** 图谱节点（扩展实体，包含计算属性） */
export interface GraphNode extends Entity {
  relationCount: number
  radius: number
  opacity: number
  borderStyle: 'solid' | 'dashed'
  hasGlow: boolean
  position?: { x: number; y: number; z: number }
}

/** 图谱连线 */
export interface GraphEdge {
  id: string
  sourceId: string
  targetId: string
  sourceName: string
  targetName: string
  relation: string
  confidence: number
  lineStyle: 'solid' | 'dashed' | 'dotted'
  opacity: number
  raw?: Relationship
}

/** 渲染模式 */
export type RenderMode = 'auto' | '2d' | '3d'

/** 设备性能等级 */
export type PerformanceLevel = 'high' | 'medium' | 'low'

/** 图谱状态 */
interface GraphState {
  nodes: Map<string, GraphNode>
  edges: Map<string, GraphEdge>
  isLoading: boolean
  isInitialized: boolean
  error: string | null
  entityOffset: number
  relationOffset: number
  hasMoreEntities: boolean
  hasMoreRelations: boolean
  totalEntities: number
  totalRelations: number
  userId: string | null
  projectId: string | null
  renderMode: RenderMode
  actualMode: '2d' | '3d'
  performanceLevel: PerformanceLevel
  batchSize: number
}

/** 图谱操作 */
interface GraphActions {
  loadInitial: (userId: string, projectId?: string) => Promise<void>
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
  reset: () => void
  setRenderMode: (mode: RenderMode) => void
  detectPerformance: () => PerformanceLevel
  getNodesArray: () => GraphNode[]
  getEdgesArray: () => GraphEdge[]
  getProgress: () => { loaded: number; total: number; percent: number }
  getNodeById: (id: string) => GraphNode | undefined
}

// ==================== 常量配置 ====================

const NODE_SIZE_CONFIG = {
  baseRadius: 20,
  scaleFactor: 8,
  maxIncrement: 30,
  minRadius: 20,
  maxRadius: 50,
}

const CONFIDENCE_THRESHOLDS = {
  high: 0.8,
  medium: 0.5,
}

const DEFAULT_BATCH_SIZE = 20

// ==================== 工具函数 ====================

function calculateRadius(relationCount: number): number {
  const { baseRadius, scaleFactor, maxIncrement, minRadius, maxRadius } = NODE_SIZE_CONFIG
  const increment = Math.min(Math.log2(relationCount + 1) * scaleFactor, maxIncrement)
  return Math.max(minRadius, Math.min(maxRadius, baseRadius + increment))
}

function calculateOpacity(confidence: number): number {
  if (confidence >= CONFIDENCE_THRESHOLDS.high) return 1.0
  if (confidence >= CONFIDENCE_THRESHOLDS.medium) return 0.7
  return 0.4
}

function calculateBorderStyle(confidence: number): 'solid' | 'dashed' {
  return confidence >= CONFIDENCE_THRESHOLDS.medium ? 'solid' : 'dashed'
}

function calculateHasGlow(confidence: number): boolean {
  return confidence >= CONFIDENCE_THRESHOLDS.high
}

function calculateLineStyle(confidence: number): 'solid' | 'dashed' | 'dotted' {
  if (confidence >= CONFIDENCE_THRESHOLDS.high) return 'solid'
  if (confidence >= CONFIDENCE_THRESHOLDS.medium) return 'dashed'
  return 'dotted'
}

function detectDevicePerformance(): PerformanceLevel {
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
  
  if (!gl) return 'low'
  
  const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info')
  const renderer = debugInfo 
    ? (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) 
    : ''
  
  const isHighEndGPU = /nvidia|radeon|intel.*(iris|uhd|xe)/i.test(renderer)
  const isLowEndGPU = /mali|adreno.*(3|4)|powervr|sgx/i.test(renderer)
  
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 4
  const cores = navigator.hardwareConcurrency || 4
  
  if (isHighEndGPU && memory >= 8 && cores >= 4) return 'high'
  if (isLowEndGPU || memory <= 2 || cores <= 2) return 'low'
  return 'medium'
}

function determineActualMode(renderMode: RenderMode, performanceLevel: PerformanceLevel): '2d' | '3d' {
  if (renderMode === '2d') return '2d'
  if (renderMode === '3d') return '3d'
  return performanceLevel === 'low' ? '2d' : '3d'
}

// ==================== Store 定义 ====================

export const useGraphStore = create<GraphState & GraphActions>((set, get) => ({
  // 初始状态
  nodes: new Map(),
  edges: new Map(),
  isLoading: false,
  isInitialized: false,
  error: null,
  entityOffset: 0,
  relationOffset: 0,
  hasMoreEntities: true,
  hasMoreRelations: true,
  totalEntities: 0,
  totalRelations: 0,
  userId: null,
  projectId: null,
  renderMode: 'auto',
  actualMode: '3d',
  performanceLevel: 'medium',
  batchSize: DEFAULT_BATCH_SIZE,

  /**
   * 首次加载数据 - 使用专用图谱API保证数据一致性
   */
  loadInitial: async (userId: string, projectId?: string) => {
    set({
      isLoading: true,
      error: null,
      userId,
      projectId: projectId || null,
      nodes: new Map(),
      edges: new Map(),
      entityOffset: 0,
      relationOffset: 0,
      hasMoreEntities: false, // 图谱API一次性返回所有数据
      hasMoreRelations: false,
    })

    try {
      // 使用专用图谱API，保证edges引用的节点一定在nodes中
      const graphData = await memoryAdminApi.getUserGraph(userId, projectId)
      
      console.log('📊 [GraphStore] 专用图谱API返回:', JSON.stringify({
        nodeCount: graphData.nodes.length,
        edgeCount: graphData.edges.length,
        stats: graphData.stats || 'stats字段缺失，使用节点/边长度作为备用',
        sampleEdges: graphData.edges.slice(0, 3) // 显示前3条边的完整结构
      }, null, 2))

      const nodes = new Map<string, GraphNode>()
      const edges = new Map<string, GraphEdge>()
      const relationCountMap = new Map<string, number>()
      
      // 先统计每个节点的关系数量
      graphData.edges.forEach((edge) => {
        if (edge.source) {
          relationCountMap.set(edge.source, (relationCountMap.get(edge.source) || 0) + 1)
        }
        if (edge.target) {
          relationCountMap.set(edge.target, (relationCountMap.get(edge.target) || 0) + 1)
        }
      })

      // 处理节点数据
      graphData.nodes.forEach((apiNode: ApiGraphNode) => {
        const confidence = apiNode.confidence ?? 0.8
        const relationCount = relationCountMap.get(apiNode.id) || 0
        
        // 将API节点转换为扩展的GraphNode
        const node: GraphNode = {
          id: apiNode.id,
          name: apiNode.name,
          type: apiNode.type,
          confidence: apiNode.confidence,
          properties: apiNode.properties,
          // 填充扩展字段（GraphNode extends Entity的部分）
          user_id: '', // API节点不包含这些字段，用默认值
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          // 计算属性
          relationCount,
          radius: calculateRadius(relationCount),
          opacity: calculateOpacity(confidence),
          borderStyle: calculateBorderStyle(confidence),
          hasGlow: calculateHasGlow(confidence),
        }
        
        nodes.set(apiNode.id, node)
      })
      
      // 处理边数据
      graphData.edges.forEach((edge, index) => {
        const edgeId = edge.id || `edge-${index}`
        const confidence = edge.confidence ?? 0.8
        
        // 从节点数据中获取名称
        const sourceNode = nodes.get(edge.source)
        const targetNode = nodes.get(edge.target)
        
        edges.set(edgeId, {
          id: edgeId,
          sourceId: edge.source,
          targetId: edge.target,
          sourceName: sourceNode?.name || edge.source,
          targetName: targetNode?.name || edge.target,
          relation: edge.label || edge.relation_name || edge.type || '相关',
          confidence,
          lineStyle: calculateLineStyle(confidence),
          opacity: calculateOpacity(confidence),
        })
      })

      console.log('📊 [GraphStore] 数据处理完成:', JSON.stringify({
        processedNodes: nodes.size,
        processedEdges: edges.size,
        sampleProcessedEdges: Array.from(edges.values()).slice(0, 3).map(e => ({
          id: e.id,
          relation: e.relation,
          sourceName: e.sourceName,
          targetName: e.targetName
        })),
        relationCountMap: Array.from(relationCountMap.entries()).slice(0, 5)
      }, null, 2))

      // 检测性能并设置渲染模式
      const performanceLevel = detectDevicePerformance()
      const { renderMode } = get()
      const actualMode = determineActualMode(renderMode, performanceLevel)

      set({
        nodes,
        edges,
        isLoading: false,
        isInitialized: true,
        totalEntities: graphData.stats?.node_count || graphData.nodes.length,
        totalRelations: graphData.stats?.edge_count || graphData.edges.length,
        performanceLevel,
        actualMode,
      })
    } catch (error) {
      console.error('📊 [GraphStore] 加载失败:', error)
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : '加载失败',
      })
    }
  },

  /**
   * 加载更多数据 - 图谱API一次性返回所有数据，不支持分页
   */
  loadMore: async () => {
    const { hasMoreEntities, hasMoreRelations } = get()
    
    // 图谱API一次性返回所有数据，没有更多数据可加载
    if (!hasMoreEntities && !hasMoreRelations) {
      console.log('📊 [GraphStore] 图谱API已加载所有数据，无更多数据')
      return
    }
    
    // 如果还有更多数据，重新加载全部（这种情况通常不应该发生）
    console.log('📊 [GraphStore] 重新加载图谱数据')
    const { userId, projectId } = get()
    if (userId) {
      await get().loadInitial(userId, projectId || undefined)
    }
  },

  /**
   * 刷新数据
   */
  refresh: async () => {
    const { userId, projectId } = get()
    if (userId) {
      await get().loadInitial(userId, projectId || undefined)
    }
  },

  /**
   * 重置状态
   */
  reset: () => {
    set({
      nodes: new Map(),
      edges: new Map(),
      isLoading: false,
      isInitialized: false,
      error: null,
      entityOffset: 0,
      relationOffset: 0,
      hasMoreEntities: true,
      hasMoreRelations: true,
      totalEntities: 0,
      totalRelations: 0,
      userId: null,
      projectId: null,
    })
  },

  /**
   * 设置渲染模式
   */
  setRenderMode: (mode: RenderMode) => {
    const { performanceLevel } = get()
    const actualMode = determineActualMode(mode, performanceLevel)
    set({ renderMode: mode, actualMode })
  },

  /**
   * 检测设备性能
   */
  detectPerformance: () => {
    const performanceLevel = detectDevicePerformance()
    const { renderMode } = get()
    const actualMode = determineActualMode(renderMode, performanceLevel)
    set({ performanceLevel, actualMode })
    return performanceLevel
  },

  /**
   * 获取节点数组
   */
  getNodesArray: () => {
    return Array.from(get().nodes.values())
  },

  /**
   * 获取边数组
   */
  getEdgesArray: () => {
    return Array.from(get().edges.values())
  },

  /**
   * 获取加载进度
   */
  getProgress: () => {
    const { nodes, edges, totalEntities, totalRelations } = get()
    const loaded = nodes.size + edges.size
    const total = Math.max(totalEntities + totalRelations, loaded)
    const percent = total > 0 ? Math.round((loaded / total) * 100) : 0
    return { loaded, total, percent }
  },

  /**
   * 根据 ID 获取节点
   */
  getNodeById: (id: string) => {
    return get().nodes.get(id)
  },
}))

